// @ts-check
/**
 * **Field の組み立て**（Issue #522。`duelEditor/core/` ＝ 舞台の決まりごと）。契約は `types.d.ts`。
 *
 * ここは「Field とは何か」だけを持つ。**盤がどこにあるかは知らない** ——
 * 渡すのは **ゾーンのキー → 平面の 4 点**の表だけで、それをどう作るかは実装（`duelEditor/fields/`）の仕事:
 * 実写（`fields/live.js`）/ 仮想（`fields/virtual.js`）/ 空（`EMPTY_FIELD`）。
 *
 * **どの実装もこの `toField()` 1 つから出る**ので口が欠けない
 * （旧演出プレビュー（Issue #639 で削除）では「3 実装が同じ顔をしていること」を手で合わせていて、
 * 実際に片方だけ null を返して呼ぶ側が `geometry` の真偽で判定する不具合になった）。
 */
import { CARD_ASPECT, parseZoneKey, sideOf } from './zones';

/** @typedef {import('./types').Field} Field */
/** @typedef {import('./types').Quad} Quad */

/** 平面の既定の大きさ（マット 2 枚ぶん。`fields/live.js` の `LIVE_PLANE` と同じ値） */
const DEFAULT_PLANE = Object.freeze({ w: 1080, h: 1260 });

/** 同梱プレイマットのカードの幅（平面の座標）。ゾーンから測れないときの `unit` */
export const DEFAULT_UNIT = 110;

/**
 * **幅 w・高さ h のものを平面の中心に合わせて置く**（平面の座標に写す関数を返す）。
 *
 * 元動画を平面に入れるのがこれ（`fields/live.js::videoToPlane`）。実装（`fields/`）ではなく
 * ここに置いてあるのは、**テーマ（`themes/`）も同じ計算が要る**ため —— テーマが `fields/` を
 * 読むと「見た目」が「盤がどこにあるか」に依存してしまう（`boundary.test.js` が見張る）。
 *
 * @param {{size: {w: number, h: number}|null|undefined, plane: {w: number, h: number}, scale?: number}} opts
 * @returns {(pt: number[]) => number[]}
 */
export function centerToPlane({ size, plane, scale = 1 }) {
  const w = size && size.w > 0 ? size.w : plane.w;
  const h = size && size.h > 0 ? size.h : plane.h;
  const cx = plane.w / 2; const cy = plane.h / 2;
  return (pt) => [cx + (pt[0] - w / 2) * scale, cy + (pt[1] - h / 2) * scale];
}

/**
 * **元の映像の要素（`video.el`）が、元の映像のどの矩形か**（Issue #808）。
 *
 * `video = { el, size, crop? }`: `size` は**元の映像の寸法**（盤の位置・平面への置き方はこれで決まる）、`crop` は要素が持っている
 * 元の映像の矩形（無ければ全体）。要素が元の映像の一部だけのとき（前の描く側 /fx-render が平面に入る部分だけを渡していた）に使う。
 * 要素の画素の大きさは `crop` と違ってよい（縮めて渡す）。
 */
const elPixels = (el, crop) => ({
  w: el?.videoWidth || el?.naturalWidth || el?.width || crop.w,
  h: el?.videoHeight || el?.naturalHeight || el?.height || crop.h,
});
const cropOf = (video) => {
  const size = video?.size;
  if (!(size?.w > 0) || !(size?.h > 0)) return null;
  const c = video?.crop;
  return c && c.w > 0 && c.h > 0 ? c : { x: 0, y: 0, w: size.w, h: size.h };
};

/**
 * 元の映像を平面に中心を合わせて置いたときの、**要素の矩形**（平面の座標）。テーマの実写の層と three に直接貼る経路が使う
 * @returns {{x: number, y: number, w: number, h: number} | null}
 */
export function videoPlaneRect(video, plane) {
  const crop = cropOf(video);
  if (!crop || !plane) return null;
  const [x, y] = centerToPlane({ size: video.size, plane })([crop.x, crop.y]);
  return { x, y, w: crop.w, h: crop.h };
}

/**
 * 元の映像の座標の矩形 → **要素の画素の矩形**（`drawImage` の切り出し元）。要素が**丸ごと持っていない**なら null
 * （一部だけ写すと引き伸ばされるので。実写の手札の帯は平面の外＝描く側の要素には無い）
 * @returns {{x: number, y: number, w: number, h: number} | null}
 */
export function videoSourceRect(video, rect) {
  const crop = cropOf(video);
  if (!crop || !video?.el || !rect) return null;
  if (rect.x < crop.x || rect.y < crop.y || rect.x + rect.w > crop.x + crop.w || rect.y + rect.h > crop.y + crop.h) return null;
  const px = elPixels(video.el, crop);
  const kx = px.w / crop.w; const ky = px.h / crop.h;
  return { x: (rect.x - crop.x) * kx, y: (rect.y - crop.y) * ky, w: rect.w * kx, h: rect.h * ky };
}

/**
 * `centerToPlane` の逆（平面の座標 → そのものの座標）。
 * @param {{size: {w: number, h: number}|null|undefined, plane: {w: number, h: number}, scale?: number}} opts
 * @returns {(pt: number[]) => number[]}
 */
export function planeToCenter({ size, plane, scale = 1 }) {
  const w = size && size.w > 0 ? size.w : plane.w;
  const h = size && size.h > 0 ? size.h : plane.h;
  const s = scale || 1;
  return (pt) => [(pt[0] - plane.w / 2) / s + w / 2, (pt[1] - plane.h / 2) / s + h / 2];
}

/**
 * **契約の口の一覧**（`types.d.ts` の `Field` と 1 対 1）。
 * 実装が欠かしていないことを `fields/contract.test.js` が見る。
 */
export const FIELD_PORT = Object.freeze([
  'known', 'virtual', 'plane', 'unit', 'keys', 'zones', 'zonePlane', 'cardAngle', 'planeCorners', 'side',
]);

/**
 * **そのゾーンに置かれたカードの向き**の既定（平面の中での回し。ラジアン）。
 *
 * 2 人の盤を**手前（P1）から見る**なら、奥（P2）のカードは向こうを向いている＝ 180°。
 * 実写の合成動画も（P2 のマットを 180° 回して積んである）、仮想の理想のグリッドも
 * （`idealPosition` が P2 を原点まわりに 180° 回す）この規則どおり。
 *
 * **盤の敷き方が違う Field は `cardAngles` で上書きする**（完成動画のように盤ごと 90° 倒すなど）。
 */
const defaultCardAngle = (key) => (parseZoneKey(key)?.player === 1 ? Math.PI : 0);

const finite = (v) => typeof v === 'number' && Number.isFinite(v);
const okQuad = (q) => Array.isArray(q) && q.length === 4 && q.every((p) => Array.isArray(p) && p.length === 2 && finite(p[0]) && finite(p[1]));

/** 4 点の外接矩形 */
function bounds(quad) {
  const xs = quad.map((p) => p[0]); const ys = quad.map((p) => p[1]);
  const x = Math.min(...xs); const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** 中央値（偶数個なら上側）。空なら null */
function median(list) {
  if (!list.length) return null;
  const s = [...list].sort((a, b) => a - b);
  return s[s.length >> 1];
}

/**
 * ゾーンの表からカード 1 枚の幅を測る（モンスターゾーンの短い辺の中央値）。
 * 測れなければ `DEFAULT_UNIT`
 */
function measureUnit(quads) {
  const widths = [];
  Object.entries(quads).forEach(([key, quad]) => {
    if (!key.startsWith('monster:')) return;
    const [a, b, c] = quad;
    const w = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const h = Math.hypot(c[0] - b[0], c[1] - b[1]);
    widths.push(Math.min(w, h));
  });
  return median(widths) ?? DEFAULT_UNIT;
}

/**
 * Field を作る。**3 実装すべてがここを通る。**
 *
 * @param {object} opts 省略可
 * @param {Record<string, any>} [opts.quads] ゾーンのキー → 平面の 4 点（左上・右上・右下・左下）。潰れた 4 点は落とす
 * @param {{w: number, h: number}} [opts.plane] フィールド平面の大きさ
 * @param {number|null} [opts.unit] カード 1 枚の幅（平面の座標）。省略するとゾーンから測る
 * @param {Record<string, number>} [opts.cardAngles] ゾーンのキー → カードの向き（ラジアン）。
 *   **省略した分は「奥のプレイヤーは 180°」**（`defaultCardAngle`）
 * @param {boolean} [opts.virtual]
 * @returns {Field}
 */
export function toField({ quads = {}, plane = DEFAULT_PLANE, unit = null, virtual = false, cardAngles = {} } = {}) {
  const w = plane?.w > 0 ? plane.w : DEFAULT_PLANE.w;
  const h = plane?.h > 0 ? plane.h : DEFAULT_PLANE.h;
  const size = Object.freeze({ w, h });
  /** @type {Record<string, Quad>} */
  const ok = {};
  Object.entries(quads ?? {}).forEach(([key, quad]) => { if (okQuad(quad)) ok[key] = /** @type {Quad} */ (quad.map((p) => [p[0], p[1]])); });
  const keys = Object.freeze(Object.keys(ok));
  const zones = Object.freeze(Object.fromEntries(keys.map((k) => [k, Object.freeze(bounds(ok[k]))])));
  const copy = (key) => (key && ok[key] ? /** @type {Quad} */ (ok[key].map((p) => [p[0], p[1]])) : null);
  return Object.freeze({
    known: keys.length > 0,
    virtual: Boolean(virtual),
    plane: size,
    unit: unit != null && unit > 0 ? unit : measureUnit(ok),
    keys,
    zones,
    zonePlane: copy,
    cardAngle: (key) => (finite(cardAngles?.[key]) ? cardAngles[key] : defaultCardAngle(key)),
    planeCorners: () => /** @type {Quad} */ ([[0, 0], [w, 0], [w, h], [0, h]]),
    side: sideOf,
  });
}

/** ゾーンが分からない試合の Field（**null を返さない**ので、呼ぶ側は `field.known` で判定する） */
export const EMPTY_FIELD = toField();

export { CARD_ASPECT };
